import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Gauge, Timer, Clock, Building2, ChevronDown, Loader2 } from "lucide-react";

const RANGES = [[7, "7 giorni"], [30, "30 giorni"], [90, "90 giorni"]];
const URG_LABEL = (v) => (v >= 3.5 ? "Emergenza" : v >= 2.5 ? "Alta" : v >= 1.5 ? "Normale" : "Bassa");
const URG_COLOR = (v) => (v >= 3.5 ? "bg-red-500" : v >= 2.5 ? "bg-orange-500" : v >= 1.5 ? "bg-amber-400" : "bg-slate-300");

const fmtHours = (h) => {
  if (h === null || h === undefined) return "—";
  if (h < 1) return `${Math.round(h * 60)} min`;
  if (h < 48) return `${h % 1 === 0 ? h : h.toFixed(1)} h`;
  return `${(h / 24).toFixed(1)} gg`;
};

const KpiCard = ({ icon: Icon, label, value, accent, testid, hint }) => (
  <div data-testid={testid} className="bg-card border border-border rounded-md p-5">
    <div className="flex items-center justify-between mb-3">
      <span className="text-[11px] uppercase tracking-[0.18em] font-semibold text-muted-foreground">{label}</span>
      <Icon className={`h-5 w-5 ${accent}`} strokeWidth={2} />
    </div>
    <div className="font-head text-3xl font-extrabold tracking-tighter">{value}</div>
    {hint && <div className="text-[11px] text-muted-foreground mt-1.5">{hint}</div>}
  </div>
);

export const AdvancedAnalytics = () => {
  const [days, setDays] = useState(30);
  const [buildingId, setBuildingId] = useState("tutti");
  const [buildings, setBuildings] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/buildings").then((r) => setBuildings(r.data || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/analytics/advanced", { params: { days, building_id: buildingId } });
      setData(r.data);
    } catch (e) { console.error("Errore analytics avanzate:", e); }
    finally { setLoading(false); }
  }, [days, buildingId]);
  useEffect(() => { load(); }, [load]);

  const sla = data?.sla || {};

  return (
    <section data-testid="advanced-analytics" className="border-t border-border pt-8">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#007BFF] font-semibold mb-1">Avanzate</div>
          <h2 className="font-head text-2xl font-extrabold tracking-tight">Analytics avanzate</h2>
          <p className="text-sm text-muted-foreground mt-1">SLA, tempi di gestione e performance per condominio.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex bg-secondary rounded-md p-1" data-testid="analytics-range">
            {RANGES.map(([d, lbl]) => (
              <button
                key={d}
                data-testid={`analytics-range-${d}`}
                onClick={() => setDays(d)}
                className={`text-xs font-medium px-3 py-1.5 rounded transition-colors ${days === d ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>
                {lbl}
              </button>
            ))}
          </div>
          <div className="relative">
            <select
              data-testid="analytics-building-filter"
              value={buildingId}
              onChange={(e) => setBuildingId(e.target.value)}
              className="appearance-none bg-secondary text-sm font-medium rounded-md pl-3 pr-8 py-2 outline-none cursor-pointer hover:bg-secondary/70 transition-colors max-w-[220px] truncate">
              <option value="tutti">Tutti i condomìni</option>
              {buildings.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
            <ChevronDown className="h-4 w-4 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground" />
          </div>
        </div>
      </div>

      {loading && !data ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-10"><Loader2 className="h-4 w-4 animate-spin" />Caricamento KPI…</div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="analytics-kpi-cards">
            <KpiCard testid="kpi-sla" icon={Gauge} accent="text-emerald-600"
              label="Risolte entro SLA"
              value={sla.pct_entro_sla === null || sla.pct_entro_sla === undefined ? "—" : `${sla.pct_entro_sla}%`}
              hint={sla.risolte_valutate ? `${sla.entro_sla}/${sla.risolte_valutate} pratiche risolte` : "Nessuna pratica risolta nel periodo"} />
            <KpiCard testid="kpi-resolution-time" icon={Timer} accent="text-[#007BFF]"
              label="Tempo medio risoluzione"
              value={fmtHours(sla.tempo_medio_risoluzione_h)}
              hint="Dalla apertura alla chiusura" />
            <KpiCard testid="kpi-first-response" icon={Clock} accent="text-indigo-500"
              label="Tempo medio 1ª risposta"
              value={fmtHours(sla.tempo_medio_prima_risposta_h)}
              hint="Dalla apertura alla prima azione" />
            <KpiCard testid="kpi-totals" icon={Building2} accent="text-purple-500"
              label="Pratiche nel periodo"
              value={data?.totale ?? 0}
              hint={`${data?.aperte ?? 0} aperte · ${data?.risolte ?? 0} risolte`} />
          </div>

          <div className="bg-card border border-border rounded-md overflow-hidden" data-testid="analytics-buildings-table">
            <div className="px-5 py-3 border-b border-border flex items-center gap-2">
              <Building2 className="h-4 w-4 text-muted-foreground" />
              <h3 className="font-head text-sm font-semibold">Classifica condomìni</h3>
              <span className="ml-auto text-[11px] text-muted-foreground">Per numero di pratiche</span>
            </div>
            {(data?.per_condominio || []).length === 0 ? (
              <div className="px-5 py-8 text-sm text-muted-foreground text-center" data-testid="analytics-buildings-empty">
                Nessuna pratica nel periodo selezionato.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[11px] uppercase tracking-wider text-muted-foreground border-b border-border">
                      <th className="text-left font-semibold px-5 py-2.5">Condominio</th>
                      <th className="text-right font-semibold px-3 py-2.5">Pratiche</th>
                      <th className="text-right font-semibold px-3 py-2.5">Aperte</th>
                      <th className="text-right font-semibold px-3 py-2.5">Risolte</th>
                      <th className="text-left font-semibold px-5 py-2.5 w-44">Urgenza media</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data?.per_condominio || []).map((b, i) => (
                      <tr key={b.building_id || `none-${i}`} data-testid={`analytics-building-row-${i}`} className="border-b border-border/60 last:border-0 hover:bg-secondary/40 transition-colors">
                        <td className="px-5 py-3 font-medium truncate max-w-[240px]">{b.building_name}</td>
                        <td className="px-3 py-3 text-right font-semibold">{b.totale}</td>
                        <td className="px-3 py-3 text-right text-amber-600">{b.aperte}</td>
                        <td className="px-3 py-3 text-right text-emerald-600">{b.risolte}</td>
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-2 bg-secondary rounded-full overflow-hidden max-w-[90px]">
                              <div className={`h-full ${URG_COLOR(b.urgenza_media)} rounded-full`} style={{ width: `${(b.urgenza_media / 4) * 100}%` }} />
                            </div>
                            <span className="text-xs text-muted-foreground w-20">{URG_LABEL(b.urgenza_media)}</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </section>
  );
};
